/**
 * @file escalation-comment-modal.tsx
 * @module app/modals/escalation-comment-modal
 * @description Renders the escalation comment modal modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Modal, Button, Form } from 'react-bootstrap';
import { useState } from 'react';

interface EscalationCommentModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: { comment: string; action: 'resolve' | 'reject' }) => void;
  action: 'resolve' | 'reject';
  isLoading?: boolean;
}

const EscalationCommentModal = ({
  show,
  onHide,
  onSubmit,
  action,
  isLoading = false,
}: EscalationCommentModalProps) => {
  const [comment, setComment] = useState('');

  const handleSubmit = () => {
    if (!comment.trim()) {
      return;
    }
    onSubmit({ comment: comment.trim(), action });
    setComment('');
  };

  const handleClose = () => {
    setComment('');
    onHide();
  };

  const actionLabel = action === 'resolve' ? 'Resolve' : 'Reject';

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="modal-lg">
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fs-18 fw-bolder">
          {actionLabel} Escalation
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="d-flex flex-column mb-4">
            <Form.Label className="fs-16 fw-500 text-dark mb-2">
              Add Comment <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              className="form-control border-r8px fs-15"
              placeholder="Enter your comment or reason for this action..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isLoading}
            />
            <small className="text-gray-600 mt-2">
              Please provide a comment to explain your decision
            </small>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button
          variant="light"
          className="uw-btn uw-btn-light fs-16 fw-500"
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          className="uw-btn uw-btn-primary fs-16 fw-500"
          onClick={handleSubmit}
          disabled={!comment.trim() || isLoading}
        >
          {isLoading ? 'Processing...' : `${actionLabel} Escalation`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EscalationCommentModal;
